import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  OnDestroy,
  inject,
  signal,
} from '@angular/core';
import { Router, RouterLink } from '@angular/router';
import { DOCUMENT } from '@angular/common';
import { AuthenticationService } from 'src/app/_services/authentication.service';
import { EDIT_ACCESS_ROLE_LIST, LAB_ADMIN_ROLE_LIST, OGI_ADMIN_AND_SUPER_USER_ROLE_LIST, SUPER_ADMIN_ROLE_LIST, Role } from 'src/app/_models/role';
import { Laboratory } from 'src/app/_models/laboratory';
import { FormBase } from 'src/app/_models/form-base';
import { FormBuilder } from '@angular/forms';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { RoleCheckPipe } from '../../_helpers/role-check.pipe';
import { DropdownFormFieldComponent } from '../forms/dropdown-form-field/dropdown-form-field.component';
import { NavButtonComponent } from '../buttons/nav-button/nav-button.component';
import { ErrorLogService } from 'src/app/_services/error-log.service';
import { DeviceService } from 'src/app/_services/device.service';
import { ExperimentService } from 'src/app/_services/experiment.service';
import { update } from '@intercom/messenger-js-sdk';

@Component({
  selector: 'app-navbar',
  templateUrl: './navbar.component.html',
  styleUrls: ['./navbar.component.scss'],
  imports: [
    NavButtonComponent,
    RouterLink,
    DropdownFormFieldComponent,
    RoleCheckPipe,
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class NavbarComponent implements OnDestroy {
  private readonly authenticationService = inject(AuthenticationService);
  private readonly errorLogService = inject(ErrorLogService);
  private readonly deviceService = inject(DeviceService);
  private readonly experimentService = inject(ExperimentService);
  private readonly router = inject(Router);
  private readonly formBuilder = inject(FormBuilder);
  private readonly document = inject(DOCUMENT);
  private readonly changeDetectorRef = inject(ChangeDetectorRef);

  private readonly destroy$ = new Subject<void>();

  sidebarCollapsed = true;
  /** Submenu the user just clicked into; hides it (overriding :hover) until the pointer/focus leaves. */
  readonly dismissedSubmenu = signal<string | null>(null);
  // Permissions
  currentUser = this.authenticationService.currentUserValue;
  Role = Role;
  readonly editAccessRoles = EDIT_ACCESS_ROLE_LIST;
  readonly labAdminRoles = LAB_ADMIN_ROLE_LIST;
  readonly ogiAdminAndSuperUserRoles = OGI_ADMIN_AND_SUPER_USER_ROLE_LIST;
  readonly superAdminRoles = SUPER_ADMIN_ROLE_LIST;
  selectedLab: Laboratory;
  labList: Laboratory[];
  currentOrganisation: number;

  hasLabs =
    this.currentUser?.laboratories && this.currentUser?.laboratories.length > 0;

  laboratorySelectField = new FormBase<number>({
    key: 'labSelect',
    label: '',
    type: 'select',
    placeholder: 'Select lab',
    disabled: false,
    required: false,
    value: null,
    options: [],
  });

  labSelectForm = this.formBuilder.group({
    [this.laboratorySelectField.key]: [''],
  });

  constructor() {
    this.selectedLab = this.authenticationService.selectedLaboratory;

    this.authenticationService.currentOrg$
      .pipe(takeUntil(this.destroy$))
      .subscribe((org) => {
        this.currentOrganisation = org;
      });

    // Rebuild the lab list whenever currentUser changes, so the dropdown
    // picks up labs joined/created mid-session (e.g. a Super User adding
    // themselves to a lab, or creating a new one) without needing a reload.
    // Ignore the null emitted on logout — this component still reads
    // currentUser.role unguarded elsewhere, and briefly stays mounted while
    // the logout navigation resolves.
    this.authenticationService.currentUser$
      .pipe(takeUntil(this.destroy$))
      .subscribe((user) => {
        if (!user) {
          return;
        }
        this.currentUser = user;
        this.hasLabs = !!user.laboratories && user.laboratories.length > 0;
        this.refreshLabList();
        // This update can originate from a component outside the navbar's
        // own template (e.g. the Add Laboratory button on a different page),
        // so there's no DOM event here for OnPush to pick up on its own.
        this.changeDetectorRef.markForCheck();
      });

    // Subscribe to changes
    this.authenticationService.selectedLab$
      .pipe(takeUntil(this.destroy$))
      .subscribe((lab) => {
        this.selectedLab = lab;

        const labInOwnOrg = this.labList?.find(
          (x) => x.laboratoryId === this.selectedLab?.laboratoryId,
        );

        const selectLabToReturnOptionIndex =
          this.laboratorySelectField.options.findIndex((x) => x.key === '0');

        // If Ogi_Super_Administrator loads into lab view from a different organisation add additional dropdown option to lab options
        if (
          !labInOwnOrg &&
          selectLabToReturnOptionIndex === -1 &&
          this.currentUser.role !== Role.Super_User
        ) {
          this.laboratorySelectField.options.push({
            key: '0',
            value: 'Select lab to return',
          });
        }

        // Set dropdown value
        if (!labInOwnOrg) {
          this.labSelectForm.controls[this.laboratorySelectField.key].setValue(
            '0',
          );
        } else {
          this.labSelectForm.controls[this.laboratorySelectField.key].setValue(
            this.selectedLab?.laboratoryId?.toString(),
          );
        }

        // Remove additional dropdown option in lab options if selected lab is in lablist
        if (labInOwnOrg && selectLabToReturnOptionIndex !== -1) {
          this.laboratorySelectField.options.splice(
            selectLabToReturnOptionIndex,
            1,
          );
        }

        // Clear device/experiment/error log table caches when lab context changes
        this.deviceService.onUpdatePageSize(null);
        this.experimentService.onUpdatePageSize(null);
        this.errorLogService.onUpdatePageSize(null);

        // Update Intercom with new lab context
        if (lab) {
          update({
            "laboratory": lab.laboratoryName,
          })
        }
      });

    // Now subscribe to value changes in the lab select form
    this.labSelectForm.valueChanges.subscribe((val) => {
      const labId = val.labSelect;
      const selectedLab = this.labList.find(
        (lab) => lab.laboratoryId === parseInt(labId),
      );
      if (selectedLab && selectedLab !== this.selectedLab) {
        this.authenticationService.setCurrentOrg(
          this.currentUser.organisationId,
        );
        this.authenticationService.setSelectedLaboratory(selectedLab);
        // as lab context has changed, navigate to dashboard
        if (
          this.currentUser.role === Role.Super_User &&
          !this.selectedLab.isOwnLab
        ) {
          void this.router.navigate(['/laboratories/view-lab']);
        } else {
          void this.router.navigate(['/dashboard']);
        }
      }
    });
  }

  private refreshLabList(): void {
    this.labList = this.currentUser?.laboratories;
    if (this.labList && this.labList.length > 0) {
      this.laboratorySelectField.options = this.labList.map((lab) => {
        return {
          key: lab.laboratoryId.toString(),
          value: lab.laboratoryName + (lab.isOwnLab ? ' - Member' : ''),
        };
      });
      // Let's set the default lab to selectedLab. This is a display sync,
      // not a user action, so suppress the event — otherwise it's
      // indistinguishable from a manual dropdown pick to the valueChanges
      // subscriber below and triggers an unwanted navigation.
      this.labSelectForm.controls[this.laboratorySelectField.key].setValue(
        this.selectedLab?.laboratoryId?.toString(),
        { emitEvent: false },
      );
    }
  }

  buttonClicked(): void {
    this.sidebarCollapsed = !this.sidebarCollapsed;
  }

  dismissSubmenu(name: string): void {
    this.dismissedSubmenu.set(name);
    // Without this, a mouse click leaves the clicked button focused, which keeps
    // :focus-within true and reopens the submenu as soon as the dismissed flag
    // above is cleared on mouseleave.
    (this.document.activeElement as HTMLElement)?.blur();
  }

  clearDismissed(): void {
    this.dismissedSubmenu.set(null);
  }

  async onLogout(): Promise<void> {
    await this.authenticationService.logout();
    void this.router.navigate(['/login']);
  }

  // Check if user has super user is vewing a lab they are not a member of
  checkSuperUserPermissions(): boolean {
    return (
      this.currentUser.role === Role.Super_User && !this.selectedLab?.isOwnLab
    );
  }

  public get containerClasses(): string {
    return this.sidebarCollapsed
      ? 'container--collapsed'
      : 'container--expanded';
  }

  public get labContainerClasses(): string {
    return this.sidebarCollapsed ? 'lab-container' : 'lab-container--expanded';
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }
}
